/**
 * Security Page - HAL 3000
 * Poste Sécurité: Détecteurs Incendie, Capteurs, Caméras Aqara G100
 * VOYANTS INCENDIE GÉANTS + ALERTE
 */
import { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/App';
import { 
  Shield, AlertTriangle, DoorOpen, Eye, Volume2, Phone,
  Lock, Unlock, Bell, History, Home, DoorClosed, Camera, RefreshCw, Flame
} from 'lucide-react';

// Generate placeholder noise pattern when no snapshot available
const generateNoisePattern = (seed) => {
  const canvas = document.createElement('canvas');
  canvas.width = 320;
  canvas.height = 240;
  const ctx = canvas.getContext('2d');
  
  ctx.fillStyle = '#1a1a1a';
  ctx.fillRect(0, 0, 320, 240);
  
  const imageData = ctx.getImageData(0, 0, 320, 240);
  const data = imageData.data;
  
  for (let i = 0; i < data.length; i += 4) {
    const noise = Math.random() * 40 + 10;
    data[i] = noise;
    data[i + 1] = noise;
    data[i + 2] = noise;
  }
  
  ctx.putImageData(imageData, 0, 0);
  
  ctx.fillStyle = 'rgba(255, 100, 0, 0.8)';
  ctx.font = '14px monospace';
  ctx.fillText('EN ATTENTE HOMEKIT...', 80, 120);
  ctx.font = '10px monospace';
  ctx.fillText('Configurez le Raccourci iOS', 90, 140);
  
  return canvas.toDataURL();
};

// Camera snapshot component - Real snapshots from HomeKit via iOS Shortcuts
const CameraSnapshot = ({ camera, onRefresh }) => {
  const hasRealSnapshot = camera.image_base64 && camera.image_base64.length > 100;
  const imageSrc = hasRealSnapshot 
    ? (camera.image_base64.startsWith('data:') ? camera.image_base64 : `data:image/jpeg;base64,${camera.image_base64}`)
    : generateNoisePattern(camera.camera_id || '0');
  
  const updatedAt = camera.updated_at ? new Date(camera.updated_at) : null;
  const isStale = updatedAt && (Date.now() - updatedAt.getTime() > 120000); // >2min = stale
  
  return (
    <div className="bg-black border border-gray-700 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-2 py-1 bg-gray-900 border-b border-gray-700">
        <div className="flex items-center gap-1">
          <Camera className="w-3 h-3 text-green-400" />
          <span className="text-white text-xs font-mono truncate">{camera.camera_name || camera.name}</span>
        </div>
        <div className="flex items-center gap-1">
          {updatedAt && (
            <span className={`text-[8px] font-mono ${isStale ? 'text-orange-400' : 'text-green-400'}`}>
              {updatedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button onClick={onRefresh} className="p-1 hover:bg-gray-700 rounded">
            <RefreshCw className="w-3 h-3 text-gray-400" />
          </button>
        </div>
      </div>
      <div className="relative aspect-video bg-black">
        <img 
          src={imageSrc} 
          alt={camera.camera_name || camera.name} 
          className="w-full h-full object-cover" 
        />
        {hasRealSnapshot && (
          <div className="absolute top-1 left-1 flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${isStale ? 'bg-orange-500' : 'bg-green-500 animate-pulse'}`} />
            <span className={`text-[8px] font-mono ${isStale ? 'text-orange-400' : 'text-green-400'}`}>
              {isStale ? 'STALE' : 'LIVE'}
            </span>
          </div>
        )}
        {!hasRealSnapshot && (
          <div className="absolute top-1 left-1 flex items-center gap-1">
            <div className="w-2 h-2 bg-red-500 rounded-full" />
            <span className="text-red-400 text-[8px] font-mono">OFFLINE</span>
          </div>
        )}
        <div className="absolute bottom-1 right-1 text-cyan-400/50 text-[8px] font-mono">AQARA G100</div>
      </div>
    </div>
  );
};

// ÉNORME VOYANT INCENDIE - Clignote si alerte
const FireDetectorPanel = ({ detector, isTriggered, onTriggerAlert }) => {
  const [blinking, setBlinking] = useState(false);
  
  // Animation de clignotement si déclenché
  useEffect(() => {
    if (isTriggered) {
      const interval = setInterval(() => setBlinking(b => !b), 300);
      return () => clearInterval(interval);
    }
    setBlinking(false);
  }, [isTriggered]);
  
  // Extraire le nom simplifié
  const shortName = detector.name
    .replace('Détecteur ', '')
    .replace('fumée ', '')
    .replace('incendie ', '')
    .toUpperCase();
  
  return (
    <div 
      className={`relative rounded-xl p-4 transition-all duration-300 ${
        isTriggered 
          ? blinking 
            ? 'bg-red-600 scale-105' 
            : 'bg-red-900 scale-100'
          : detector.online 
            ? 'bg-gray-800' 
            : 'bg-gray-900 opacity-50'
      }`}
      style={{
        boxShadow: isTriggered 
          ? blinking 
            ? '0 0 60px rgba(239, 68, 68, 0.9), 0 0 100px rgba(239, 68, 68, 0.5)' 
            : '0 0 30px rgba(239, 68, 68, 0.6)'
          : detector.online 
            ? '0 0 20px rgba(34, 197, 94, 0.2)' 
            : 'none',
        border: isTriggered 
          ? '4px solid #ef4444' 
          : detector.online 
            ? '3px solid #22c55e' 
            : '3px solid #333'
      }}
      data-testid={`fire-detector-${detector.id}`}
    >
      {/* ÉNORME ICÔNE */}
      <div className="flex flex-col items-center gap-2">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
          isTriggered 
            ? blinking ? 'bg-red-500' : 'bg-red-700' 
            : detector.online ? 'bg-green-900/50' : 'bg-gray-800'
        }`}>
          <Flame className={`w-12 h-12 ${
            isTriggered ? 'text-white' : detector.online ? 'text-green-400' : 'text-gray-600'
          }`} />
        </div>
        
        {/* Nom */}
        <div className={`text-center font-mono font-bold text-lg ${
          isTriggered ? 'text-white' : 'text-amber-200'
        }`}>
          {shortName}
        </div>
        
        {/* Status */}
        <div className={`px-4 py-1 rounded-full text-sm font-bold ${
          isTriggered 
            ? 'bg-white text-red-600 animate-pulse' 
            : detector.online 
              ? 'bg-green-900/50 text-green-400' 
              : 'bg-gray-800 text-gray-500'
        }`}>
          {isTriggered ? 'ALERTE !' : detector.online ? 'OK' : 'HORS LIGNE'}
        </div>
      </div>
      
      {/* BOUTON TEST (simulation) */}
      {detector.online && !isTriggered && (
        <button 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onTriggerAlert(detector.id);
          }}
          className="absolute bottom-2 right-2 px-2 py-1 text-[10px] bg-yellow-900/50 text-yellow-400 rounded font-mono hover:bg-yellow-900"
          type="button"
        >
          TEST
        </button>
      )}
    </div>
  );
};

// Capteur de sécurité (porte, mouvement)
const SecuritySensorPanel = ({ sensor }) => {
  const isMotion = sensor.category === 'pir' || sensor.name?.toLowerCase().includes('mouvement');
  const isOpen = sensor.status?.some(s => s.code === 'doorcontact_state' && s.value === true);
  
  return (
    <div 
      className={`rounded-lg p-3 border-2 ${
        sensor.online 
          ? isOpen 
            ? 'bg-orange-900/30 border-orange-500' 
            : 'bg-gray-800/50 border-green-600' 
          : 'bg-gray-900/50 border-gray-700 opacity-50'
      }`}
      data-testid={`security-sensor-${sensor.id}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
          sensor.online 
            ? isOpen ? 'bg-orange-600' : 'bg-green-900' 
            : 'bg-gray-800'
        }`}>
          {isMotion 
            ? <Eye className={`w-6 h-6 ${sensor.online ? 'text-cyan-400' : 'text-gray-600'}`} />
            : isOpen 
              ? <DoorOpen className="w-6 h-6 text-orange-200" />
              : <DoorClosed className="w-6 h-6 text-green-400" />
          }
        </div>
        
        <div className="flex-1">
          <div className={`font-mono font-bold text-sm ${
            sensor.online ? 'text-white' : 'text-gray-500'
          }`}>
            {sensor.name}
          </div>
          <div className={`text-xs ${
            sensor.online 
              ? isOpen ? 'text-orange-400' : 'text-green-400' 
              : 'text-gray-600'
          }`}>
            {sensor.online 
              ? isMotion ? 'Actif' : isOpen ? 'OUVERT' : 'FERMÉ'
              : 'Hors ligne'
            }
          </div>
        </div>
        
        <div className={`w-3 h-3 rounded-full ${
          sensor.online ? 'bg-green-500' : 'bg-gray-600'
        }`} />
      </div>
    </div>
  );
};

const Security = () => {
  const [houseStatus, setHouseStatus] = useState({ house_occupied: true, heating_locked: false });
  const [fireDetectors, setFireDetectors] = useState([]);
  const [securitySensors, setSecuritySensors] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [alarmArmed, setAlarmArmed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState(false);
  const [triggeredDetectors, setTriggeredDetectors] = useState([]);

  const SECURITY_CODE = '8648';

  const loadCameras = useCallback(async () => {
    try {
      const response = await api.get('/cameras/snapshots');
      if (response.data.success && response.data.cameras) {
        setCameras(response.data.cameras);
      }
    } catch (error) {
      console.error('Error loading cameras:', error);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const statusRes = await api.get('/heating/safety/status');
      if (statusRes.data?.success) {
        setHouseStatus(statusRes.data);
        setAlarmArmed(!statusRes.data.house_occupied);
      }

      const devicesRes = await api.get('/integrations/tuya/devices');
      if (devicesRes.data?.success) {
        const allDevices = devicesRes.data.devices || [];
        
        // Filtrer les détecteurs incendie (category: ywbj = détecteur fumée)
        const fireDevices = allDevices.filter(d => {
          const name = (d.name || '').toLowerCase();
          const category = d.category || '';
          return category === 'ywbj' || 
                 name.includes('fumée') || 
                 name.includes('incendie') || 
                 name.includes('smoke') || 
                 name.includes('fire');
        });
        setFireDetectors(fireDevices);
        
        // Filtrer les capteurs de sécurité (portes, mouvement)
        const sensorDevices = allDevices.filter(d => {
          const name = (d.name || '').toLowerCase();
          const category = d.category || '';
          // pir = mouvement, mcs = contact magnétique
          return category === 'pir' || category === 'mcs' ||
                 name.includes('capteur') || 
                 name.includes('mouvement') ||
                 name.includes('porte') || 
                 name.includes('fenêtre') ||
                 name.includes('ouverture');
        });
        setSecuritySensors(sensorDevices);
      }
    } catch (error) {
      console.error('Error loading security data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadCameras();
    const interval = setInterval(loadData, 300000); // 5 minutes (économie API)
    const cameraInterval = setInterval(loadCameras, 30000); // Refresh cameras every 30s
    return () => {
      clearInterval(interval);
      clearInterval(cameraInterval);
    };
  }, [loadData, loadCameras]);

  // Trigger fire alert (simulation pour test)
  const handleTriggerAlert = (detectorId) => {
    setTriggeredDetectors(prev => [...prev, detectorId]);
    
    // Trouver le nom du détecteur (utiliser .id pas .device_id)
    const detector = fireDetectors.find(d => d.id === detectorId);
    const detectorName = detector?.name || 'Zone inconnue';
    
    // Dispatch fire alert event avec le nom correct
    window.dispatchEvent(new CustomEvent('trigger-fire-alert', {
      detail: { detectorId, room: detectorName }
    }));
    
    setEvents(prev => [{
      time: new Date().toLocaleTimeString('fr-FR'),
      type: 'fire',
      message: `Alerte incendie: ${detectorName} (TEST)`
    }, ...prev.slice(0, 9)]);
    
    // Auto-reset après 10 secondes
    setTimeout(() => {
      setTriggeredDetectors(prev => prev.filter(id => id !== detectorId));
    }, 10000);
  };

  const handleArmDisarm = () => {
    setShowCodeInput(true);
    setCodeInput('');
    setCodeError(false);
  };

  const handleKeypadClick = (num) => {
    if (codeInput.length < 4) {
      const newCode = codeInput + num;
      setCodeInput(newCode);
      setCodeError(false);
      
      if (newCode.length === 4) {
        setTimeout(async () => {
          if (newCode === SECURITY_CODE) {
            try {
              const newOccupied = alarmArmed;
              await api.post('/heating/safety/occupancy', { occupied: newOccupied });
              setAlarmArmed(!alarmArmed);
              setHouseStatus(prev => ({ ...prev, house_occupied: newOccupied }));
              setShowCodeInput(false);
              setCodeInput('');
              setEvents(prev => [{
                time: new Date().toLocaleTimeString('fr-FR'),
                type: newOccupied ? 'disarm' : 'arm',
                message: newOccupied ? 'Alarme désactivée' : 'Alarme activée'
              }, ...prev.slice(0, 9)]);
            } catch (error) {
              console.error('Error toggling alarm:', error);
            }
          } else {
            setCodeError(true);
            setCodeInput('');
          }
        }, 300);
      }
    }
  };

  return (
    <Layout title="Poste Sécurité">
      <div className="h-full flex flex-col gap-2 overflow-hidden">
        {/* Status Banner */}
        <div className={`rounded-lg p-2 border-2 flex-shrink-0 ${
          alarmArmed ? 'bg-red-900/30 border-red-500' : 'bg-green-900/30 border-green-500'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                alarmArmed ? 'bg-red-500' : 'bg-green-500'
              }`}>
                {alarmArmed ? <Shield className="w-4 h-4 text-white" /> : <Home className="w-4 h-4 text-white" />}
              </div>
              <div>
                <div className={`text-base font-bold font-mono ${alarmArmed ? 'text-red-400' : 'text-green-400'}`}>
                  {alarmArmed ? 'ALARME ACTIVÉE' : 'MAISON OCCUPÉE'}
                </div>
              </div>
            </div>
            <button
              onClick={handleArmDisarm}
              data-testid="arm-disarm-btn"
              className={`px-4 py-2 rounded-lg font-bold font-mono text-sm transition-all ${
                alarmArmed ? 'bg-green-600 hover:bg-green-500 text-white' : 'bg-red-600 hover:bg-red-500 text-white'
              }`}
            >
              {alarmArmed ? <><Unlock className="w-4 h-4 inline mr-1" /> DÉSACTIVER</> : <><Lock className="w-4 h-4 inline mr-1" /> ACTIVER</>}
            </button>
          </div>
        </div>

        {/* Code Input Modal */}
        {showCodeInput && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
            <div className="bg-gray-900 border-2 border-cyan-500 rounded-xl p-6 w-72">
              <h3 className="text-cyan-400 font-bold text-center mb-4 font-mono text-sm">
                {alarmArmed ? 'DÉSACTIVATION' : 'ACTIVATION'}
              </h3>
              <div className="flex justify-center gap-2 mb-4">
                {[0, 1, 2, 3].map(i => (
                  <div key={i} className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center text-xl font-bold ${
                    codeError ? 'border-red-500 bg-red-900/30' :
                    codeInput.length > i ? 'border-cyan-500 bg-cyan-900/30 text-cyan-400' : 'border-gray-600 bg-gray-800'
                  }`}>
                    {codeInput.length > i ? '•' : ''}
                  </div>
                ))}
              </div>
              {codeError && <div className="text-red-400 text-center text-xs mb-3 font-mono">CODE INCORRECT</div>}
              <div className="grid grid-cols-3 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, 'C'].map((num, i) => (
                  num !== null ? (
                    <button
                      key={i}
                      onClick={() => num === 'C' ? setCodeInput('') : handleKeypadClick(num.toString())}
                      className={`h-12 rounded-lg font-bold text-lg transition-all ${
                        num === 'C' ? 'bg-red-900/50 text-red-400 hover:bg-red-900' : 'bg-gray-800 text-white hover:bg-gray-700'
                      }`}
                    >
                      {num}
                    </button>
                  ) : <div key={i} />
                ))}
              </div>
              <button onClick={() => setShowCodeInput(false)} className="w-full mt-3 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 text-sm">
                Annuler
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 grid grid-cols-12 gap-3 min-h-0 overflow-hidden">
          
          {/* LEFT: DÉTECTEURS INCENDIE GÉANTS */}
          <div className="col-span-4 flex flex-col gap-2 overflow-hidden">
            <div className="text-white font-bold text-lg flex items-center gap-2 px-2">
              <Flame className="w-6 h-6 text-orange-500" />
              DÉTECTEURS INCENDIE
            </div>
            
            <div className="flex-1 overflow-auto p-2">
              {loading ? (
                <div className="text-gray-500 text-center py-8">Chargement...</div>
              ) : fireDetectors.length === 0 ? (
                <div className="text-gray-500 text-center py-8">Aucun détecteur trouvé</div>
              ) : (
                <div className="grid gap-4">
                  {fireDetectors.map(detector => (
                    <FireDetectorPanel 
                      key={detector.device_id} 
                      detector={detector}
                      isTriggered={triggeredDetectors.includes(detector.device_id)}
                      onTriggerAlert={handleTriggerAlert}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* CENTER: CAMÉRAS */}
          <div className="col-span-4 flex flex-col gap-2 overflow-hidden">
            <div className="text-white font-bold text-lg flex items-center gap-2 px-2">
              <Camera className="w-5 h-5 text-green-400" />
              CAMÉRAS AQARA G100
              <a 
                href="/camera-setup.html" 
                target="_blank"
                className="ml-auto text-[10px] text-cyan-400 hover:text-cyan-300 bg-cyan-900/30 px-2 py-1 rounded border border-cyan-700 hover:border-cyan-500 transition-all"
              >
                ⚙️ CONFIGURER
              </a>
            </div>
            <div className="flex-1 grid grid-cols-1 gap-2 overflow-auto p-2">
              {cameras.length > 0 ? (
                cameras.map(camera => (
                  <CameraSnapshot 
                    key={camera.camera_id} 
                    camera={camera} 
                    onRefresh={loadCameras}
                  />
                ))
              ) : (
                <>
                  {/* Placeholders en attente de config */}
                  <CameraSnapshot 
                    camera={{ camera_id: 'cam-1', camera_name: 'Caméra 1', image_base64: null }} 
                    onRefresh={loadCameras}
                  />
                  <CameraSnapshot 
                    camera={{ camera_id: 'cam-2', camera_name: 'Caméra 2', image_base64: null }} 
                    onRefresh={loadCameras}
                  />
                </>
              )}
            </div>
          </div>

          {/* RIGHT: CAPTEURS + ACTIONS */}
          <div className="col-span-4 flex flex-col gap-2 overflow-hidden">
            {/* Capteurs */}
            <div className="flex-1 bg-black/40 border border-gray-700 rounded-lg p-2 overflow-hidden flex flex-col">
              <h3 className="text-white font-bold text-sm mb-2 flex items-center gap-2 flex-shrink-0">
                <Eye className="w-4 h-4 text-cyan-400" />
                CAPTEURS SÉCURITÉ ({securitySensors.length})
              </h3>
              
              <div className="flex-1 overflow-auto space-y-2">
                {loading ? (
                  <div className="text-gray-500 text-center py-4 text-sm">Chargement...</div>
                ) : securitySensors.length === 0 ? (
                  <div className="text-gray-500 text-center py-4 text-sm">Aucun capteur détecté</div>
                ) : (
                  securitySensors.map(sensor => (
                    <SecuritySensorPanel key={sensor.device_id} sensor={sensor} />
                  ))
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="bg-black/40 border border-gray-700 rounded-lg p-2 flex-shrink-0">
              <h3 className="text-white font-bold text-sm mb-2 flex items-center gap-2">
                <Bell className="w-4 h-4 text-yellow-400" />
                ACTIONS ALARME
              </h3>
              <div className="space-y-1">
                <div className={`p-2 rounded flex items-center gap-2 text-xs ${alarmArmed ? 'bg-green-900/30 text-green-400' : 'bg-gray-800 text-gray-500'}`}>
                  <Volume2 className="w-3 h-3" /> <span>Annonce Alexa</span>
                  {alarmArmed && <span className="ml-auto text-[10px]">ACTIF</span>}
                </div>
                <div className={`p-2 rounded flex items-center gap-2 text-xs ${alarmArmed ? 'bg-green-900/30 text-green-400' : 'bg-gray-800 text-gray-500'}`}>
                  <Phone className="w-3 h-3" /> <span>Appel police simulé</span>
                  {alarmArmed && <span className="ml-auto text-[10px]">ACTIF</span>}
                </div>
              </div>
            </div>

            {/* History */}
            <div className="bg-black/40 border border-gray-700 rounded-lg p-2 flex-shrink-0">
              <h3 className="text-white font-bold text-sm mb-2 flex items-center gap-2">
                <History className="w-4 h-4 text-purple-400" />
                HISTORIQUE
              </h3>
              {events.length === 0 ? (
                <div className="text-gray-500 text-xs text-center py-2">Aucun événement</div>
              ) : (
                <div className="space-y-1 max-h-24 overflow-auto">
                  {events.slice(0, 5).map((event, i) => (
                    <div key={i} className={`p-1.5 rounded text-[10px] ${
                      event.type === 'fire' ? 'bg-orange-900/30 text-orange-400' :
                      event.type === 'arm' ? 'bg-red-900/30 text-red-400' : 'bg-green-900/30 text-green-400'
                    }`}>
                      <span className="font-mono">{event.time}</span> - {event.message}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Security;
